import React from 'react'
import { FleetUiStrings } from '../i18n/fleetI18n'

interface ControlPrioritiesPanelProps {
  strings: FleetUiStrings
}

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '6px 10px',
  fontSize: 12,
  color: '#374151',
  borderBottom: '1px solid #e5e7eb',
}
const td: React.CSSProperties = {
  padding: '6px 10px',
  fontSize: 13,
  color: '#111827',
  borderBottom: '1px solid #f1f5f9',
  verticalAlign: 'top',
}

// Read-only badge. "active" reuses the green "in sync / Appliqué" colors used
// elsewhere in the fleet UI (DivergencePanel); "in development" is neutral grey.
const Badge: React.FC<{ label: string; active: boolean }> = ({
  label,
  active,
}) => (
  <span
    style={{
      display: 'inline-block',
      padding: '1px 8px',
      borderRadius: 10,
      fontSize: 12,
      whiteSpace: 'nowrap',
      background: active ? '#f0fdf4' : '#f3f4f6',
      color: active ? '#15803d' : '#6b7280',
    }}
  >
    {label}
  </span>
)

// Read-only informational panel describing the UL20xx luminaire dimming-source
// priority (Appendix 1 "Luminaire control chart"). The device checks all
// dimming sources by priority and applies the highest-ranked active one to the
// DALI / 0–10V output. Sources not yet implemented are flagged "En développement".
const ControlPrioritiesPanel: React.FC<ControlPrioritiesPanelProps> = ({
  strings,
}) => {
  // Ordered highest priority (1) first.
  const rows: {
    name: string
    condition: string
    effect: string
    active: boolean
  }[] = [
    {
      name: strings.cpManualName,
      condition: strings.cpManualCondition,
      effect: strings.cpManualEffect,
      active: false,
    },
    {
      name: strings.cpLdrName,
      condition: strings.cpLdrCondition,
      effect: strings.cpLdrEffect,
      active: false,
    },
    {
      name: strings.cpCalendarName,
      condition: strings.cpCalendarCondition,
      effect: strings.cpCalendarEffect,
      active: true,
    },
    {
      name: strings.cpDigName,
      condition: strings.cpDigCondition,
      effect: strings.cpDigEffect,
      active: false,
    },
    {
      name: strings.cpProfileName,
      condition: strings.cpProfileCondition,
      effect: strings.cpProfileEffect,
      active: true,
    },
    {
      name: strings.cpDefaultName,
      condition: strings.cpDefaultCondition,
      effect: strings.cpDefaultEffect,
      active: false,
    },
  ]

  return (
    <div>
      <h4 style={{ marginTop: 0, fontSize: 15, color: '#111827' }}>
        {strings.controlPriorities}
      </h4>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr>
            <th style={{ ...th, width: 60 }}>{strings.cpColPriority}</th>
            <th style={th}>{strings.cpColSource}</th>
            <th style={th}>{strings.cpColCondition}</th>
            <th style={th}>{strings.cpColEffect}</th>
            <th style={th}>{strings.cpColStatus}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.name}>
              <td style={{ ...td, color: '#6b7280' }}>{index + 1}</td>
              <td style={{ ...td, fontWeight: 600 }}>{row.name}</td>
              <td style={td}>{row.condition}</td>
              <td style={td}>{row.effect}</td>
              <td style={td}>
                <Badge
                  active={row.active}
                  label={
                    row.active ? strings.cpBadgeActive : strings.cpBadgeInDev
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280' }}>
        {strings.cpFooter}
      </div>
      <div style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>
        {strings.cpClampNote}
      </div>
    </div>
  )
}

export default ControlPrioritiesPanel
